import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, RefreshCw, ChevronDown, ChevronUp, Copy, FileText, Zap, DollarSign, Clock, User, Search, X, PanelLeftClose, PanelLeft, Maximize2, Pencil, Eye, Database, Coins, Gift, MessageSquare, ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import { getSystemPromptAgent, hasSystemPromptMapping } from "../config/agentMapping";
import { formatInTimezone, formatShort } from "../utils/timezone";
import AdminMenu from "../components/AdminMenu";

const PromptLog = () => {
  const navigate = useNavigate();
  const { userId } = useParams(); // Optional userId from URL
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Get user's timezone preference
  const userTimezone = user?.timezone || "UTC";
  
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [targetUser, setTargetUser] = useState(null);
  
  // Scroll anchor ref
  const scrollAnchorRef = useRef(null);
  
  // Search state
  const [promptSearch, setPromptSearch] = useState("");

  // Session Search — ONE input, executes on Enter.
  //   * If the query starts with `user_` → backend does an owner search
  //     (all sessions where user_id == query).
  //   * Otherwise → backend searches session titles across
  //     chat_sessions + system_sessions.
  //   Empty query → results panel collapses back to the loaded sessions.
  const [dbSearchQuery, setDbSearchQuery] = useState("");
  const [dbSearchResults, setDbSearchResults] = useState([]);
  const [isDbSearching, setIsDbSearching] = useState(false);
  const [showDbSearchResults, setShowDbSearchResults] = useState(false);
  
  // Monitor state - persist to localStorage
  const [monitorQuery, setMonitorQuery] = useState(() => {
    const saved = localStorage.getItem("plog_monitor_query");
    return saved || "";
  });
  const [isMonitorFilterActive, setIsMonitorFilterActive] = useState(() => {
    const saved = localStorage.getItem("plog_monitor_active");
    return saved === "true";
  });
  
  // Persist monitor state to localStorage
  useEffect(() => {
    localStorage.setItem("plog_monitor_query", monitorQuery);
  }, [monitorQuery]);
  
  useEffect(() => {
    localStorage.setItem("plog_monitor_active", isMonitorFilterActive.toString());
  }, [isMonitorFilterActive]);
  
  // Session search — fires on Enter only. No live/debounced fetching.
  const searchDatabase = async (query) => {
    const q = (query || "").trim();
    if (q.length < 2) {
      setDbSearchResults([]);
      setShowDbSearchResults(false);
      return;
    }

    setIsDbSearching(true);
    try {
      const response = await authAxios.get(`/admin/plog/search?q=${encodeURIComponent(q)}&limit=50`);
      setDbSearchResults(response.data.sessions || []);
      setShowDbSearchResults(true);
    } catch (error) {
      console.error("Session search failed:", error);
      setDbSearchResults([]);
      setShowDbSearchResults(true);  // still switch view so user sees "no results"
    } finally {
      setIsDbSearching(false);
    }
  };
  
  // Full session prompts for search (loaded when search/monitor is active)
  const [allSessionPrompts, setAllSessionPrompts] = useState([]);
  const [loadingAllPrompts, setLoadingAllPrompts] = useState(false);
  const [hasLoadedAllPrompts, setHasLoadedAllPrompts] = useState(false);
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [summaryDropdownOpen, setSummaryDropdownOpen] = useState(false);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const PAGE_SIZE = 30;
  
  // Check for scroll anchor from URL params (returning from full screen)
  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");
    const sessionId = searchParams.get("session");
    
    if (sessionId && !selectedSession && !loading) {
      // Auto-select the session when returning from full screen
      setSelectedSession(sessionId);
      fetchSessionPromptsWithScroll(sessionId, scrollTo);
    }
  }, [searchParams, loading, selectedSession]);
  
  // Fetch prompts with scroll anchor support
  const fetchSessionPromptsWithScroll = async (sessionId, scrollTo) => {
    try {
      setLoadingPrompts(true);
      setCurrentSkip(0);
      
      const response = await authAxios.get(`/admin/plog/session/${sessionId}?limit=${PAGE_SIZE}&skip=0`);
      setPrompts(response.data.prompts || []);
      setSummary(response.data.summary || null);
      setTotalCount(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      setExpandedPrompts({});
      
      // Scroll to the prompt after a short delay
      if (scrollTo) {
        setTimeout(() => {
          const element = document.getElementById(`prompt-${scrollTo}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            // Highlight briefly
            element.classList.add("ring-2", "ring-divine-gold/50");
            setTimeout(() => {
              element.classList.remove("ring-2", "ring-divine-gold/50");
            }, 2000);
          }
        }, 500);
      }
    } catch (error) {
      console.error("Error fetching session prompts:", error);
      toast.error("Failed to load prompts");
    } finally {
      setLoadingPrompts(false);
    }
  };

  // Filter prompts based on search or monitor
  // Uses allSessionPrompts when search/monitor is active, otherwise paginated prompts
  const filteredPrompts = useMemo(() => {
    // Determine which prompt list to use
    const searchPrompts = (promptSearch.trim() || monitorQuery.trim()) && hasLoadedAllPrompts 
      ? allSessionPrompts 
      : prompts;
    
    // If monitor filter is active, use monitor query
    if (isMonitorFilterActive && monitorQuery.trim()) {
      const search = monitorQuery.toLowerCase();
      return searchPrompts.filter(prompt =>
        prompt.content?.toLowerCase().includes(search) ||
        prompt.agent_name?.toLowerCase().includes(search) ||
        prompt.prompt_type?.toLowerCase().includes(search) ||
        prompt.prompt_id?.toLowerCase().includes(search) ||
        prompt.model?.toLowerCase().includes(search)
      );
    }
    // Otherwise use regular search
    if (!promptSearch.trim()) return prompts; // Return paginated when no search
    const search = promptSearch.toLowerCase();
    return searchPrompts.filter(prompt =>
      prompt.content?.toLowerCase().includes(search) ||
      prompt.agent_name?.toLowerCase().includes(search) ||
      prompt.prompt_type?.toLowerCase().includes(search) ||
      prompt.prompt_id?.toLowerCase().includes(search) ||
      prompt.model?.toLowerCase().includes(search)
    );
  }, [prompts, allSessionPrompts, hasLoadedAllPrompts, promptSearch, monitorQuery, isMonitorFilterActive]);

  // Count matches for monitor query (searches through all session prompts)
  const monitorMatchCount = useMemo(() => {
    if (!monitorQuery.trim()) return 0;
    const searchPrompts = hasLoadedAllPrompts ? allSessionPrompts : prompts;
    const search = monitorQuery.toLowerCase();
    return searchPrompts.filter(prompt =>
      prompt.content?.toLowerCase().includes(search) ||
      prompt.agent_name?.toLowerCase().includes(search) ||
      prompt.prompt_type?.toLowerCase().includes(search) ||
      prompt.prompt_id?.toLowerCase().includes(search) ||
      prompt.model?.toLowerCase().includes(search)
    ).length;
  }, [prompts, allSessionPrompts, hasLoadedAllPrompts, monitorQuery]);

  // Total prompts count for display
  const totalPromptsForSearch = useMemo(() => {
    if (hasLoadedAllPrompts) return allSessionPrompts.length;
    return totalCount;
  }, [hasLoadedAllPrompts, allSessionPrompts.length, totalCount]);

  // Handle setting monitor
  const handleSetMonitor = async () => {
    if (promptSearch.trim()) {
      const searchTerm = promptSearch;
      setMonitorQuery(searchTerm);
      setPromptSearch(""); // Clear search input
      setIsMonitorFilterActive(true); // Default to ON - show filtered results
      toast.success(`Monitoring "${searchTerm}"`);
      
      // Ensure all prompts are loaded for comprehensive monitoring
      if (!hasLoadedAllPrompts && selectedSession) {
        await loadAllSessionPrompts();
      }
    }
  };

  // Handle clearing monitor
  const handleClearMonitor = () => {
    setMonitorQuery("");
    setIsMonitorFilterActive(false);
  };

  // Toggle monitor filter
  const toggleMonitorFilter = () => {
    setIsMonitorFilterActive(!isMonitorFilterActive);
  };

  // Load all prompts for the session (for comprehensive search)
  const loadAllSessionPrompts = async () => {
    if (!selectedSession || loadingAllPrompts) return;
    if (hasLoadedAllPrompts && allSessionPrompts.length > 0) return;
    
    try {
      setLoadingAllPrompts(true);
      // Fetch all prompts (high limit to get everything)
      const response = await authAxios.get(`/admin/plog/session/${selectedSession}?limit=1000&skip=0`);
      const fetchedPrompts = response.data.prompts || [];
      setAllSessionPrompts(fetchedPrompts);
      setHasLoadedAllPrompts(true);
      console.log(`Loaded ${fetchedPrompts.length} prompts for full session search`);
    } catch (error) {
      console.error("Error loading all prompts:", error);
      // Fall back to using paginated prompts
      setAllSessionPrompts([...prompts]);
      setHasLoadedAllPrompts(true);
    } finally {
      setLoadingAllPrompts(false);
    }
  };

  // Load all prompts when search becomes active
  useEffect(() => {
    if (promptSearch.trim() && selectedSession && !hasLoadedAllPrompts && !loadingAllPrompts) {
      loadAllSessionPrompts();
    }
  }, [promptSearch, selectedSession]);

  // Reset prompts state and reload when session changes (but keep monitor)
  useEffect(() => {
    if (selectedSession) {
      setAllSessionPrompts([]);
      setHasLoadedAllPrompts(false);
      
      // If monitor is active, reload all prompts for new session
      if (monitorQuery.trim()) {
        // Small delay to ensure state is reset before loading
        setTimeout(() => {
          loadAllSessionPrompts();
        }, 100);
      }
    }
  }, [selectedSession]);

  // Get the active highlight term (search or monitor)
  const getHighlightTerm = () => {
    if (promptSearch.trim()) return promptSearch.trim();
    if (monitorQuery.trim()) return monitorQuery.trim();
    return null;
  };

  // Highlight matching text in content
  const highlightText = (text, term) => {
    if (!term || !text) return text;
    
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === term.toLowerCase()) {
        return (
          <mark 
            key={index} 
            className="bg-divine-gold/40 text-white px-0.5 rounded"
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  // Check admin access
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch sessions on mount or when userId changes
  useEffect(() => {
    fetchSessions();
  }, [userId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setSelectedSession(null);
      setPrompts([]);
      setSummary(null);
      setHasMore(false);
      setTotalCount(0);
      setCurrentSkip(0);
      
      let response;
      if (userId) {
        // Fetch sessions for specific user
        response = await authAxios.get(`/admin/plog/user/${userId}?limit=100`);
        setTargetUser({
          user_id: userId,
          name: response.data.user_name,
          email: response.data.user_email
        });
      } else {
        // Fetch all sessions
        response = await authAxios.get("/admin/plog/sessions?limit=100");
        setTargetUser(null);
      }
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionPrompts = async (sessionId, skip = 0, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingPrompts(true);
        setSelectedSession(sessionId);
      }
      
      const response = await authAxios.get(
        `/admin/plog/session/${sessionId}?limit=${PAGE_SIZE}&skip=${skip}`
      );
      
      const newPrompts = response.data.prompts || [];
      
      if (append) {
        setPrompts(prev => [...prev, ...newPrompts]);
      } else {
        setPrompts(newPrompts);
        setSummary(response.data.summary || null);
        setExpandedPrompts({});
      }
      
      setHasMore(response.data.has_more || false);
      setTotalCount(response.data.total_count || 0);
      setCurrentSkip(skip + newPrompts.length);
      
    } catch (error) {
      console.error("Error fetching prompts:", error);
      toast.error("Failed to load prompts");
    } finally {
      setLoadingPrompts(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (selectedSession && hasMore && !loadingMore) {
      fetchSessionPrompts(selectedSession, currentSkip, true);
    }
  };

  const togglePromptExpand = (index) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const formatCost = (cost) => {
    if (cost < 0.0001) return "<$0.0001";
    return `$${cost.toFixed(4)}`;
  };

  const getPromptTypeColor = (type, eventType) => {
    // Special handling for SILO:PINNED events
    if (eventType === "SILO:PINNED") {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
    switch (type) {
      case "system": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "user": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "assistant": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default: return "bg-white/10 text-white/60 border-white/20";
    }
  };

  const getPromptTypeIcon = (type, eventType) => {
    // Special handling for SILO:PINNED events
    if (eventType === "SILO:PINNED") {
      return "📌";
    }
    switch (type) {
      case "system": return "⚙️";
      case "user": return "👤";
      case "assistant": return "🤖";
      default: return "📝";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-divine-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-full mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Sidebar Toggle */}
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white/60 hover:text-white"
              title={sidebarOpen ? "Close sessions panel" : "Open sessions panel"}
            >
              {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              onClick={() => userId ? navigate(`/admin/users/${userId}`) : navigate("/admin")}
              className="text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-divine-gold" />
              <h1 className="font-cinzel text-xl text-white">PROMPT LOG</h1>
            </div>
            {targetUser && (
              <div className="flex items-center gap-2 ml-4 px-3 py-1 bg-divine-gold/10 rounded-full border border-divine-gold/30">
                <User className="w-4 h-4 text-divine-gold" />
                <span className="text-divine-gold text-sm">{targetUser.name}</span>
                <span className="text-white/40 text-xs">({targetUser.email})</span>
              </div>
            )}
          </div>
          
          {/* Right side: Summary dropdown + actions */}
          <div className="flex items-center gap-3">
            {/* Summary Dropdown in Header */}
            {summary && (
              <div className="relative">
                <button
                  onClick={() => setSummaryDropdownOpen(!summaryDropdownOpen)}
                  className="flex items-center gap-4 px-4 py-2 bg-gradient-to-r from-divine-gold/10 to-purple-500/10 border border-divine-gold/30 rounded-lg hover:from-divine-gold/20 hover:to-purple-500/20 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-divine-gold" />
                    <span className="text-white font-medium">{summary.total_tokens.toLocaleString()}</span>
                    <span className="text-white/50 text-xs">tokens</span>
                  </div>
                  <div className="w-px h-4 bg-white/20" />
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-divine-gold" />
                    <span className="text-divine-gold font-medium">{formatCost(summary.total_cost)}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${summaryDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Content */}
                {summaryDropdownOpen && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setSummaryDropdownOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-80 bg-black/95 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                      <div className="p-4 space-y-4">
                        {/* Token Breakdown */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center p-3 bg-white/5 rounded-lg">
                            <div className="text-white/50 text-xs uppercase tracking-wider">Total</div>
                            <div className="text-xl font-bold text-white mt-1">
                              {summary.total_tokens.toLocaleString()}
                            </div>
                          </div>
                          <div className="text-center p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                            <div className="text-blue-400/70 text-xs uppercase tracking-wider">Input</div>
                            <div className="text-lg font-semibold text-blue-400 mt-1">
                              {summary.total_input_tokens.toLocaleString()}
                            </div>
                          </div>
                          <div className="text-center p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                            <div className="text-green-400/70 text-xs uppercase tracking-wider">Output</div>
                            <div className="text-lg font-semibold text-green-400 mt-1">
                              {summary.total_output_tokens.toLocaleString()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Agent Breakdown */}
                        {Object.keys(summary.by_agent || {}).length > 0 && (
                          <div className="pt-3 border-t border-white/10">
                            <div className="text-white/50 text-xs uppercase tracking-wider mb-2">By Agent</div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {Object.entries(summary.by_agent).map(([agent, stats]) => (
                                <div
                                  key={agent}
                                  className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg"
                                >
                                  <span className="text-white text-sm font-medium">{agent}</span>
                                  <div className="text-white/50 text-xs">
                                    {stats.total_tokens.toLocaleString()} tok • {formatCost(stats.cost)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {userId ? (
              <Button
                variant="ghost"
                onClick={() => navigate("/plog")}
                className="text-white/60 hover:text-white text-sm"
              >
                View All Logs
              </Button>
            ) : user?.user_id && (
              <Button
                variant="ghost"
                onClick={() => navigate(`/plog/${user.user_id}`)}
                className="text-white/60 hover:text-white text-sm flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                View My Logs
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate("/plog/tokens")}
              className="text-white/60 hover:text-white"
              data-testid="tokens-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Tokens
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/plog/credits")}
              className="text-white/60 hover:text-white"
              data-testid="credits-btn"
            >
              <Coins className="w-4 h-4 mr-2" />
              Credits
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/plog/bonus-pool")}
              className="text-white/60 hover:text-white"
              data-testid="bonus-pool-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              Bonus Pool
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/coach-lint")}
              className="text-white/60 hover:text-white"
              data-testid="coach-lint-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Coach Lint
            </Button>
            <Button
              variant="ghost"
              onClick={fetchSessions}
              className="text-white/60 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <AdminMenu />
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Slide-out Sessions Panel */}
        <div 
          className={`${
            sidebarOpen ? 'w-80' : 'w-0'
          } transition-all duration-300 ease-in-out overflow-hidden border-r border-white/10 bg-black/20 flex-shrink-0`}
        >
          <div className="w-80 h-full flex flex-col">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-divine-gold" />
                  <span className="text-white text-sm font-medium">
                    {showDbSearchResults
                      ? `Sessions (${dbSearchResults.length} search results)`
                      : `Sessions (${sessions.length})`
                    }
                  </span>
                </div>
                {showDbSearchResults && (
                  <button
                    onClick={() => {
                      setShowDbSearchResults(false);
                      setDbSearchQuery("");
                      setDbSearchResults([]);
                    }}
                    className="text-white/40 hover:text-white text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
                    data-testid="clear-session-search"
                  >
                    Clear Search
                  </button>
                )}
              </div>
              {/* Session Search — Enter to execute. Searches title, or
                  owner user_id if query starts with `user_`. */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search title or user_id, press Enter…"
                  value={dbSearchQuery}
                  onChange={(e) => setDbSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      searchDatabase(dbSearchQuery);
                    }
                  }}
                  className="pl-9 pr-8 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm h-9"
                  data-testid="session-search-input"
                />
                {isDbSearching && (
                  <RefreshCw className="absolute right-8 top-1/2 -translate-y-1/2 w-3 h-3 text-divine-gold animate-spin" />
                )}
                {dbSearchQuery && (
                  <button
                    onClick={() => {
                      setDbSearchQuery("");
                      setDbSearchResults([]);
                      setShowDbSearchResults(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                    data-testid="session-search-clear"
                  >
                    <X className="w-3 h-3 text-white/40" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {/* Database Search Results */}
              {showDbSearchResults ? (
                <>
                  {dbSearchResults.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-4">
                      No sessions found matching &quot;{dbSearchQuery}&quot;
                    </p>
                  ) : (
                    dbSearchResults.map((session) => (
                      <button
                        key={session.session_id}
                        onClick={() => {
                          fetchSessionPrompts(session.session_id);
                          // Keep DB results visible - don't clear showDbSearchResults
                        }}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedSession === session.session_id
                            ? "bg-divine-gold/20 border-divine-gold/50"
                            : "bg-white/5 border-white/10 hover:bg-white/10"
                        }`}
                      >
                        <div className="font-medium text-white text-sm truncate">
                          {session.title}
                        </div>
                        {/* Created date and user as subtext */}
                        {(session.created_at || session.latest_prompt) && (
                          <div className="text-white/40 text-xs mt-0.5">
                            {formatShort(session.created_at || session.latest_prompt, userTimezone)}
                            {session.user_name && <span className="ml-2 text-white/50">• {session.user_name}</span>}
                          </div>
                        )}
                        {session.match_count > 0 && (
                          <div className="text-divine-gold text-xs mt-1">
                            Title match
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {(session.total_tokens || 0).toLocaleString()}
                          </span>
                          <span>{session.call_count || 0} calls</span>
                        </div>
                        {session.matched_agents?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {session.matched_agents.slice(0, 3).map((agent) => (
                              <span
                                key={agent}
                                className="px-1.5 py-0.5 bg-divine-gold/20 rounded text-[10px] text-divine-gold"
                              >
                                {agent}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </>
              ) : sessions.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">
                  No sessions with prompt logs yet.
                  <br />
                  Start chatting to generate logs.
                </p>
              ) : (
                sessions.map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => fetchSessionPrompts(session.session_id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedSession === session.session_id
                        ? "bg-divine-gold/20 border-divine-gold/50"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <div className="font-medium text-white text-sm truncate">
                      {session.title}
                    </div>
                    {/* Description for coaching sessions */}
                    {session.description && (
                      <div className="text-white/40 text-xs mt-0.5 truncate">
                        {session.description}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {session.total_tokens.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        {formatCost(session.total_cost)}
                      </span>
                      <span>{session.call_count} calls</span>
                    </div>
                    {/* Timestamp */}
                    {session.last_call && (
                      <div className="text-white/30 text-[10px] mt-1">
                        Last: {formatShort(session.last_call, userTimezone)}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {session.agents_used?.slice(0, 3).map((agent) => (
                        <span
                          key={agent}
                          className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] text-white/60"
                        >
                          {agent}
                        </span>
                      ))}
                      {session.agents_used?.length > 3 && (
                        <span className="text-[10px] text-white/40">
                          +{session.agents_used.length - 3}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Prompts View */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedSession ? (
            <Card className="bg-white/5 border-white/10 h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">Select a session to view prompts</p>
              </CardContent>
            </Card>
          ) : loadingPrompts ? (
            <Card className="bg-white/5 border-white/10 h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <RefreshCw className="w-8 h-8 text-divine-gold animate-spin mx-auto mb-4" />
                <p className="text-white/40">Loading prompts...</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Prompts List */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-3">
                  {/* Session Title Header */}
                  {(() => {
                    const sessionInfo = sessions.find(s => s.session_id === selectedSession);
                    const sessionTitle = sessionInfo?.title || selectedSession;
                    const sessionType = sessionInfo?.mode === "coaching" ? "Coaching" : 
                                       sessionInfo?.mode === "practice" ? "Practice" : "Chat";
                    const description = sessionInfo?.description;
                    
                    return (
                      <div className="mb-3 pb-3 border-b border-white/10">
                        <h3 className="text-white font-medium text-base truncate">
                          {sessionTitle}
                        </h3>
                        {description && (
                          <p className="text-white/50 text-xs mt-1">
                            {sessionType}: {description.replace(/\(/, " as ").replace(/\//, " and ").replace(/\)/, "")}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-white text-sm">
                        Prompts ({filteredPrompts.length}{(promptSearch || isMonitorFilterActive) ? ` of ${totalPromptsForSearch}` : ''})
                        {loadingAllPrompts && (
                          <span className="ml-2 text-divine-gold text-xs animate-pulse">loading all...</span>
                        )}
                        {(promptSearch || monitorQuery) && hasLoadedAllPrompts && (
                          <span className="ml-2 text-green-400 text-xs">✓ searching all</span>
                        )}
                      </CardTitle>
                      <Button
                        onClick={() => navigate(`/plog/chat/${selectedSession}`)}
                        variant="outline"
                        size="sm"
                        className="border-divine-gold/40 text-divine-gold hover:bg-divine-gold/10 hover:text-divine-gold h-7 px-3 text-xs"
                        data-testid="chat-review-open-btn"
                        title="Open the user/assistant chat transcript for this session"
                      >
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                        Chat Review
                      </Button>
                      <Button
                        onClick={() => navigate("/plog/audit")}
                        variant="outline"
                        size="sm"
                        className="border-divine-gold/40 text-divine-gold hover:bg-divine-gold/10 hover:text-divine-gold h-7 px-3 text-xs"
                        data-testid="audit-open-btn"
                        title="Open Commission Audit v2 (site-wide + per-user)"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                        Audit v2
                      </Button>
                      
                      {/* Monitor Badge */}
                      {monitorQuery && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={toggleMonitorFilter}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs transition-all ${
                              isMonitorFilterActive 
                                ? 'bg-divine-gold/30 text-divine-gold border border-divine-gold/50' 
                                : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                            }`}
                            title={isMonitorFilterActive ? "Click to show all prompts" : "Click to filter to monitored term"}
                          >
                            <Eye className="w-3 h-3" />
                            <span className="font-medium uppercase">{monitorQuery}</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                              isMonitorFilterActive 
                                ? 'bg-divine-gold text-divine-black' 
                                : 'bg-white/20 text-white'
                            }`}>
                              {monitorMatchCount}
                            </span>
                          </button>
                          <button
                            onClick={handleClearMonitor}
                            className="p-1 hover:bg-white/10 rounded-full transition-colors"
                            title="Clear monitor"
                          >
                            <X className="w-3 h-3 text-white/40 hover:text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Prompt Search with Monitor Button */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        type="text"
                        placeholder="Search prompts (content, agent, prompt_id, model...)"
                        value={promptSearch}
                        onChange={(e) => setPromptSearch(e.target.value)}
                        className="pl-9 pr-8 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm h-9"
                      />
                      {promptSearch && (
                        <button
                          onClick={() => setPromptSearch("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                        >
                          <X className="w-3 h-3 text-white/40" />
                        </button>
                      )}
                    </div>
                    
                    {/* Monitor Button - shows when search is active */}
                    {promptSearch.trim() && (
                      <Button
                        onClick={handleSetMonitor}
                        variant="outline"
                        size="sm"
                        className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10 h-9 px-3"
                        title="Monitor this search term"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Monitor
                      </Button>
                    )}
                  </div>
                  
                  {/* Search hint */}
                  {promptSearch && filteredPrompts.length > 0 && (
                    <p className="text-white/30 text-xs mt-1">
                      Tip: Expand prompts to search within their content
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {filteredPrompts.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-4">
                      {promptSearch ? (
                        <>No prompts matching &quot;{promptSearch}&quot;</>
                      ) : isMonitorFilterActive ? (
                        <>No prompts matching monitored term &quot;{monitorQuery}&quot;</>
                      ) : (
                        <>No prompts recorded for this session yet.</>
                      )}
                    </p>
                  ) : (
                    filteredPrompts.map((prompt, index) => {
                      // Find original index for expand state
                      const originalIndex = prompts.findIndex(p => p.log_id === prompt.log_id);
                      const expandKey = originalIndex >= 0 ? originalIndex : index;
                      const isSiloPinned = prompt.event_type === "SILO:PINNED";
                      return (
                      <div
                        key={prompt.log_id || index}
                        id={`prompt-${index}`}
                        className={`rounded-lg border ${getPromptTypeColor(prompt.prompt_type, prompt.event_type)}`}
                      >
                        {/* Prompt Header */}
                        <button
                          onClick={() => togglePromptExpand(expandKey)}
                          className="w-full flex items-center justify-between p-3 text-left"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{getPromptTypeIcon(prompt.prompt_type, prompt.event_type)}</span>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Message Number Badge */}
                                {prompt.msg_num && (
                                  <span className={`px-1.5 py-0.5 text-xs font-mono rounded border ${
                                    isSiloPinned 
                                      ? 'bg-gray-500/20 text-gray-400 border-gray-500/30' 
                                      : 'bg-divine-gold/20 text-divine-gold border-divine-gold/30'
                                  }`} title="Message number in session">
                                    #{prompt.msg_num}
                                  </span>
                                )}
                                {/* SILO:PINNED special rendering */}
                                {isSiloPinned ? (
                                  <>
                                    <span className="font-medium text-sm text-gray-400">
                                      SILO:PINNED
                                    </span>
                                    <span className="text-gray-500 text-xs">
                                      ({prompt.silo_id})
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {/* Enhanced Title: Type (agent) : DisplayName */}
                                    <span className="font-medium text-sm capitalize">
                                      {prompt.prompt_type}
                                    </span>
                                    <span className="text-white/40 text-xs">
                                      ({prompt.agent_name})
                                    </span>
                                  </>
                                )}
                                {/* Display Name - User or Match (non-silo only) */}
                                {!isSiloPinned && prompt.display && (
                                  <span className={`text-xs font-medium ${
                                    prompt.prompt_type === 'user' ? 'text-green-400' : 
                                    prompt.prompt_type === 'assistant' ? 'text-blue-400' : 'text-purple-400'
                                  }`}>
                                    : {prompt.prompt_type === 'user' && prompt.display.user_name 
                                      ? prompt.display.user_name 
                                      : prompt.prompt_type === 'assistant' && prompt.display.match_name 
                                      ? prompt.display.match_name 
                                      : prompt.display.prompt_title || ''}
                                  </span>
                                )}
                                {/* Prompt Title Badge (non-silo only) */}
                                {!isSiloPinned && prompt.display?.prompt_title && prompt.prompt_type === 'system' && (
                                  <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] rounded border border-purple-500/30">
                                    {prompt.display.prompt_title}
                                  </span>
                                )}
                                {/* Edit link - opens prompt editor filtered by mapped agent in new tab (non-silo only) */}
                                {!isSiloPinned && prompt.prompt_type === "system" && hasSystemPromptMapping(prompt.agent_name) && (
                                  <Link
                                    to={`/admin?tab=prompts&agent=${encodeURIComponent(getSystemPromptAgent(prompt.agent_name))}`}
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 hover:bg-purple-500/30 rounded text-purple-300 text-xs transition-colors"
                                    title={`Edit prompts for ${getSystemPromptAgent(prompt.agent_name)} (opens in new tab)`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                    Edit
                                  </Link>
                                )}
                              </div>
                              <div className={`text-xs mt-0.5 ${isSiloPinned ? 'text-gray-500' : 'text-white/50'}`}>
                                {prompt.model} • {prompt.total_tokens} tokens
                                {prompt.estimated_cost > 0 && ` • ${formatCost(prompt.estimated_cost)}`}
                                {/* Cache metrics */}
                                {(prompt.metadata?.cache_creation_tokens || prompt.metadata?.cache_read_tokens) && (
                                  <span className="ml-2 inline-flex items-center gap-1">
                                    <Database className="w-3 h-3 text-cyan-400 inline" />
                                    {prompt.metadata?.cache_creation_tokens > 0 && (
                                      <span className="text-orange-400" title="Cache Write">
                                        W:{prompt.metadata.cache_creation_tokens.toLocaleString()}
                                      </span>
                                    )}
                                    {prompt.metadata?.cache_read_tokens > 0 && (
                                      <span className="text-cyan-400" title="Cache Read (90% saved)">
                                        R:{prompt.metadata.cache_read_tokens.toLocaleString()}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                              {/* IDs Row - Full log_id and prompt_id/silo_id */}
                              <div className="text-white/30 text-[10px] mt-0.5 flex items-center gap-2 font-mono flex-wrap">
                                <span title="Log ID">log:{prompt.log_id}</span>
                                {!isSiloPinned && prompt.message_id && (
                                  <span className="text-blue-400/60" title={`Message ID: ${prompt.message_id}`}>
                                    msg:{prompt.message_id_short || prompt.message_id?.slice(0, 12)}
                                  </span>
                                )}
                                {/* SILO:PINNED - Link to prompt editor */}
                                {isSiloPinned && prompt.silo_id && (
                                  <Link
                                    to={`/admin?tab=prompts&promptId=${encodeURIComponent(prompt.silo_id)}`}
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-gray-300 underline"
                                    title={`Edit silo: ${prompt.silo_id}`}
                                  >
                                    Prompt:{prompt.silo_id}
                                  </Link>
                                )}
                                {!isSiloPinned && prompt.prompt_id && (
                                  <Link
                                    to={`/admin?tab=prompts&promptId=${encodeURIComponent(prompt.prompt_id)}`}
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-purple-400 hover:text-purple-300 underline"
                                    title={`Edit prompt: ${prompt.prompt_id}`}
                                  >
                                    prompt:{prompt.prompt_id}
                                  </Link>
                                )}
                              </div>
                              {/* Timestamp and User */}
                              {prompt.created_at && (
                                <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${isSiloPinned ? 'text-gray-500' : 'text-white/30'}`}>
                                  <Clock className="w-3 h-3" />
                                  {formatInTimezone(prompt.created_at, userTimezone)}
                                  {(prompt.user_name || prompt.user_id) && (
                                    <span className="ml-2">
                                      •  {prompt.user_name || "Unknown"} : {prompt.user_id || "N/A"}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Full screen button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const highlightParam = getHighlightTerm() ? `&highlight=${encodeURIComponent(getHighlightTerm())}` : '';
                                navigate(`/plog/prompt/${selectedSession}/${prompt.log_id}?index=${index}${highlightParam}`);
                              }}
                              className="p-1.5 rounded hover:bg-white/10 transition-colors"
                              title="View full screen"
                              data-testid={`fullscreen-${prompt.log_id}`}
                            >
                              <Maximize2 className="w-4 h-4 text-white/40" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(prompt.content);
                              }}
                              className="p-1.5 rounded hover:bg-white/10 transition-colors"
                              title="Copy content"
                            >
                              <Copy className="w-4 h-4 text-white/40" />
                            </button>
                            {expandedPrompts[expandKey] ? (
                              <ChevronUp className="w-4 h-4 text-white/40" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-white/40" />
                            )}
                          </div>
                        </button>

                        {/* Prompt Content (Expanded) */}
                        {expandedPrompts[expandKey] && (
                          <div className="px-3 pb-3">
                            <pre className="bg-black/30 rounded-lg p-3 text-xs text-white/80 whitespace-pre-wrap overflow-x-auto max-h-[400px] overflow-y-auto font-mono">
                              {getHighlightTerm() ? highlightText(prompt.content, getHighlightTerm()) : prompt.content}
                            </pre>
                          </div>
                        )}
                      </div>
                    )})
                  )}
                  
                  {/* Pagination Info & Load More Button */}
                  {prompts.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between text-sm text-white/50">
                        <span>
                          Showing {prompts.length} of {totalCount} prompts
                        </span>
                        {hasMore && (
                          <Button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            variant="outline"
                            size="sm"
                            className="border-white/20 hover:bg-white/10"
                          >
                            {loadingMore ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Load More ({totalCount - prompts.length} remaining)
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptLog;
